import { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router';

import {
  isCancelledError,
  QueryClient,
  useQueryClient,
} from '@tanstack/react-query';

import { meApiMeGet, updateMeApiMePatch } from '@/api/client';
import { fetchApiQuery } from '@/lib/react-query';
import { buildPathForTenant } from '@/router/paths';
import { getTenantSlug } from '@/router/tenant-routing';

import {
  TENANT_SWITCH_MIN_OVERLAY_MS,
  useTenantChange,
} from '../tenant-change-provider';
import { useAuth } from './use-auth';
import { meQueryKey } from './use-me';

export { TENANT_SWITCH_MIN_OVERLAY_MS };

const isNonMeQuery = (queryKey: readonly unknown[]) => queryKey[0] !== 'me';

/** Same-tenant callers share one in-flight sync (switch + gate race). */
let inFlightSync: { tenantId: string; promise: Promise<void> } | null = null;

/** Clears module single-flight state between unit tests. */
export function resetSyncSessionInFlightForTests(): void {
  inFlightSync = null;
}

/**
 * Persists the active tenant to the server session without updating React Query
 * cache from the PATCH response.
 */
function persistSessionTenantInBackground(tenantId: string): void {
  void updateMeApiMePatch({ saas_tenant_id: tenantId }).catch((error) => {
    // eslint-disable-next-line no-console
    console.error('Failed to PATCH session tenant:', error);
  });
}

async function clearNonMeQueries(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries({
    predicate: (query) => isNonMeQuery(query.queryKey),
  });
  queryClient.removeQueries({
    predicate: (query) => isNonMeQuery(query.queryKey),
  });
}

async function fetchAndCacheMe(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries({ queryKey: meQueryKey });

  const updatedUser = await fetchApiQuery(queryClient, {
    queryKey: meQueryKey,
    apiFn: meApiMeGet,
  });

  queryClient.setQueryData(meQueryKey, updatedUser);
}

async function runSyncSessionToUrlTenant(
  queryClient: QueryClient,
  tenantId: string
): Promise<void> {
  persistSessionTenantInBackground(tenantId);
  await clearNonMeQueries(queryClient);
  await fetchAndCacheMe(queryClient);
}

/**
 * Aligns the client `/me` cache with the URL tenant. PATCH persists the server
 * session for future cold starts; GET refreshes tenant-scoped session fields
 * (properties, roles, etc.). Clears non-`/me` query cache so stale tenant data
 * cannot render after the gate opens.
 *
 * Concurrent callers for the same `tenantId` share one in-flight promise so
 * `useSwitchTenant` and `TenantRouteGate` do not cancel each other's `/me`.
 * A sync for a different tenant replaces the in-flight entry (prior `/me` is
 * cancelled by `fetchAndCacheMe`).
 *
 * Call after the router has committed to `/~/:tenantSlug/...`.
 */
export async function syncSessionToUrlTenant(
  queryClient: QueryClient,
  tenantId: string
): Promise<void> {
  if (inFlightSync?.tenantId === tenantId) {
    return inFlightSync.promise;
  }

  const promise = runSyncSessionToUrlTenant(queryClient, tenantId).finally(
    () => {
      if (inFlightSync?.promise === promise) {
        inFlightSync = null;
      }
    }
  );
  inFlightSync = { tenantId, promise };
  return promise;
}

/**
 * @deprecated Use {@link syncSessionToUrlTenant} — cache clearing is now always applied.
 */
export async function syncSessionToUrlTenantWithCacheClear(
  queryClient: QueryClient,
  tenantId: string
): Promise<void> {
  return syncSessionToUrlTenant(queryClient, tenantId);
}

function logSyncFailure(context: string, error: unknown): void {
  if (isCancelledError(error)) {
    return;
  }
  // eslint-disable-next-line no-console
  console.error(context, error);
}

/**
 * Switches the active tenant by navigating to the same path under `/~/:slug`
 * and syncing session + client cache to the new tenant.
 */
export const useSwitchTenant = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    isSwitching,
    setIsSwitching,
    setTargetTenantName,
    setSwitchTargetTenantId,
  } = useTenantChange();
  const [isPending, setIsPending] = useState(false);

  const switchTenant = useCallback(
    async (nextTenantId: string) => {
      if (!user?.saas_tenants?.some((tenant) => tenant.id === nextTenantId)) {
        return;
      }

      const targetTenant = user.saas_tenants.find(
        (tenant) => tenant.id === nextTenantId
      );
      const nextTenantSlug = getTenantSlug(nextTenantId, user.saas_tenants);
      if (!nextTenantSlug) {
        return;
      }

      const target = buildPathForTenant(
        window.location.pathname,
        nextTenantSlug,
        window.location.search,
        window.location.hash
      );

      flushSync(() => {
        setSwitchTargetTenantId(nextTenantId);
        setTargetTenantName(targetTenant?.name ?? null);
        setIsSwitching(true);
        setIsPending(true);
      });

      flushSync(() => {
        navigate(target, { replace: true });
      });

      void syncSessionToUrlTenant(queryClient, nextTenantId).catch((error) => {
        logSyncFailure('Failed to sync tenant after switch:', error);
      });

      try {
        await new Promise((resolve) =>
          setTimeout(resolve, TENANT_SWITCH_MIN_OVERLAY_MS)
        );
      } finally {
        setIsSwitching(false);
        setTargetTenantName(null);
        setSwitchTargetTenantId(null);
        setIsPending(false);
      }
    },
    [
      navigate,
      queryClient,
      setIsSwitching,
      setSwitchTargetTenantId,
      setTargetTenantName,
      user?.saas_tenants,
    ]
  );

  return {
    switchTenant,
    isPending: isPending || isSwitching,
  };
};
